import { useCallback, useEffect, useRef, useState } from 'react'
import {
  canShowInstallPrompt,
  detectInstallPlatform,
  INSTALL_PROMPT_DELAY_MS,
  loadInstallPromptState,
  markInstalled,
  recordDismissal,
  saveInstallPromptState,
  type InstallPlatform,
} from '../installPrompt'

/** Chrome's install offer. Not in lib.dom, so it is spelled out here. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** State 1 makes the offer; state 2 is the iOS instructions, reached from state 1's primary button
 * and never entered directly. */
export type InstallPromptStep = 'offer' | 'instructions'

export interface InstallPromptSheet {
  /** Never `unsupported` — the gate has already refused those. */
  platform: Exclude<InstallPlatform, 'unsupported'>
  step: InstallPromptStep
}

export interface InstallPromptController {
  /** `null` whenever nothing is on screen. */
  sheet: InstallPromptSheet | null
  /** iOS state 1 → state 2. Expands the same sheet; it is not a dismissal and not a navigation. */
  showInstructions: () => void
  /** Android primary — hands over to Chrome's own install dialog. */
  install: () => void
  /** "Not now", and Chrome's own `dismissed` outcome. The only two things that move the ladder. */
  dismiss: () => void
  /** Called once a prediction save has actually landed. Arms the trigger and, after a beat,
   * evaluates the gate. */
  notePredictionSaved: () => void
}

/** Both halves of "already installed": the standard display-mode query, plus the non-standard flag
 * that is still the only answer in iOS Safari. */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  return (window.navigator as { standalone?: boolean }).standalone === true
}

/**
 * Owns the nudge's runtime: the stashed `beforeinstallprompt`, the delayed trigger,
 * and which of the two sheet states is showing. The rules it applies live in `src/installPrompt.ts`
 * — this file is the wiring to the browser.
 */
export function useInstallPrompt(): InstallPromptController {
  const [sheet, setSheet] = useState<InstallPromptSheet | null>(null)
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null)
  const hasSavedPrediction = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const evaluate = useCallback(() => {
    const platform = detectInstallPlatform(navigator.userAgent, {
      maxTouchPoints: navigator.maxTouchPoints,
    })
    const allowed = canShowInstallPrompt({
      platform,
      isStandalone: isStandaloneDisplay(),
      state: loadInstallPromptState(),
      hasSavedPrediction: hasSavedPrediction.current,
      hasCapturedPrompt: deferredPrompt.current !== null,
      now: Date.now(),
    })
    if (!allowed || platform === 'unsupported') return
    setSheet((current) => current ?? { platform, step: 'offer' })
  }, [])

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Suppress Chrome's own mini-infobar: the whole point is to ask at a moment the user has
      // earned, not on arrival.
      event.preventDefault()
      deferredPrompt.current = event as BeforeInstallPromptEvent
    }
    // Fires whether the install came from our sheet or from the browser's own menu, so this — not
    // only the `accepted` branch of `install()` — is what makes the flag reliable.
    const onAppInstalled = () => {
      deferredPrompt.current = null
      const state = loadInstallPromptState()
      if (state) saveInstallPromptState(markInstalled(state))
      setSheet(null)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
      if (timer.current !== null) clearTimeout(timer.current)
    }
  }, [])

  const close = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current)
      timer.current = null
    }
    setSheet(null)
  }, [])

  return {
    sheet,

    notePredictionSaved: useCallback(() => {
      hasSavedPrediction.current = true
      if (timer.current !== null) return
      timer.current = setTimeout(() => {
        timer.current = null
        evaluate()
      }, INSTALL_PROMPT_DELAY_MS)
    }, [evaluate]),

    showInstructions: useCallback(() => {
      setSheet((current) => (current ? { ...current, step: 'instructions' } : current))
    }, []),

    install: useCallback(() => {
      const event = deferredPrompt.current
      // Single-use: cleared before `prompt()` is even called, so a double tap cannot reach the
      // event a second time — Chrome throws on a second `prompt()`.
      deferredPrompt.current = null
      close()
      if (!event) return
      void event
        .prompt()
        .then(() => event.userChoice)
        .then(({ outcome }) => {
          const state = loadInstallPromptState()
          if (!state) return
          saveInstallPromptState(
            outcome === 'accepted' ? markInstalled(state) : recordDismissal(state, Date.now())
          )
        })
        .catch(() => {
          // A rejected prompt (already consumed, or the page lost user activation) is not an
          // answer from the user, so the ladder stays where it is.
        })
    }, [close]),

    dismiss: useCallback(() => {
      const state = loadInstallPromptState()
      if (state) saveInstallPromptState(recordDismissal(state, Date.now()))
      close()
    }, [close]),
  }
}
